/**
 * Format a date string to show in local timezone with both date and time
 * @param dateString - ISO date string or date value
 * @returns Formatted date string in local timezone
 */
export function formatFileDate(dateString: string | null | undefined): string {
  if (!dateString) return ""

  const date = new Date(dateString)

  // Check if date is valid
  if (isNaN(date.getTime())) return ""

  // Format as YYYY/MM/DD HH:MM
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")

  return `${year}/${month}/${day} ${hours}:${minutes}`
}

/**
 * Format a date string to show only the date part in local timezone
 * @param dateString - ISO date string or date value
 * @returns Formatted date string (date only) in local timezone
 */
export function formatFileDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return ""

  const date = new Date(dateString)

  // Check if date is valid
  if (isNaN(date.getTime())) return ""

  // Format as YYYY/MM/DD
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}/${month}/${day}`
}

/**
 * Format a date string for relative time display (for very recent files)
 * @param dateString - ISO date string or date value
 * @returns Formatted relative time string or formatted date
 */
export function formatFileDateTime(dateString: string | null | undefined): string {
  if (!dateString) return ""

  const date = new Date(dateString)

  // Check if date is valid
  if (isNaN(date.getTime())) return ""

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // If less than 24 hours ago, show relative time
  if (diffHours < 24 && diffDays === 0) {
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      if (diffMinutes < 1) return "Just now"
      return `${diffMinutes}m ago`
    }
    return `${diffHours}h ago`
  }

  // If less than 7 days ago, show days ago
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }

  // Otherwise show full date with time
  return formatFileDate(dateString)
}
